/**
 * In-panel voice/avatar call view for the Luma AI chat side panel.
 *
 * Swaps in for the chat content while a call is live. The LiveKit lifecycle is
 * owned by the shared {@link useAvatarCall} state machine and the visuals by the
 * shared {@link AvatarCallStage} (avatar video, live transcript, mic meter,
 * speakers-mode + enable-audio controls) — the same stage the sysadmin Avatar Lab
 * and the agent call dialog render. This component supplies only what is
 * panel-specific: starting the call on mount, the End-call control, and the
 * error state's way back to the chat.
 *
 * The call starts on mount: the component is only mounted by the call button's
 * click handler, so start() still runs inside the user's transient activation
 * window (which `room.startAudio()` needs for autoplay).
 *
 * 375px-safe: single column, full-width stage, 44px touch targets.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, PhoneOff } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { AvatarCallStage } from '../avatars/AvatarCallStage';
import { useAvatarCall, type MintRoomToken } from '../avatars/useAvatarCall';

export interface AvatarCallViewProps {
  /**
   * Mints the LiveKit session for this call (creates the chat session first if
   * needed and binds the avatar session to it). Owned by the panel so this view
   * stays ignorant of chat-session plumbing.
   */
  createSession: MintRoomToken;
  /** Leave the call view (after stop, or from the error state). */
  onEnd: () => void;
}

export function AvatarCallView({ createSession, onEnd }: AvatarCallViewProps) {
  const { t } = useTranslation(['aiChat', 'avatars']);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  // `tPrefix` defaults to 'call' — the tenant-facing `avatars.call.*` key set
  // (the sysadmin lab passes 'lab' for its diagnostic copy).
  const call = useAvatarCall(videoRef, audioRef);
  const { status, errorMessage, start, stop } = call;

  // Start the call on mount, and stop it on unmount.
  //
  // This effect MUST be re-runnable: StrictMode (on in dev, index.tsx) mounts →
  // unmounts → remounts, and `useAvatarCall`'s own unmount-teardown effect is
  // registered FIRST, so its cleanup fires while a first `start()` is still
  // awaiting the session mint. A run-once ref guard would let that teardown
  // supersede the in-flight start with nothing left to retry it — the call would
  // hang on "Connecting…" forever. Starting again on the remount is the
  // idiomatic fix; the panel's session factory dedupes the in-flight mint so the
  // repeat costs no second LiveKit room. `start`/`stop` are excluded from deps
  // on purpose: their identity churns with i18n readiness, and a re-run mid-call
  // would tear down a live room.
  useEffect(() => {
    void start(createSession);
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnd = () => {
    stop();
    onEnd();
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <AvatarCallStage call={call} videoRef={videoRef} audioRef={audioRef} keyPrefix="call" />

      {status === 'error' ? (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <p className="text-sm text-destructive" role="alert">
            {errorMessage}
          </p>
          <Button variant="outline" className="h-11" onClick={onEnd}>
            {t('aiChat:call.backToChat')}
          </Button>
        </div>
      ) : (
        <div className="mt-auto flex flex-col items-center gap-2 pb-2">
          {status === 'connecting' && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('aiChat:call.connecting')}
            </p>
          )}
          <Button
            variant="destructive"
            className="h-11 w-full"
            onClick={handleEnd}
            data-testid="avatar-call-end"
          >
            <PhoneOff className="h-4 w-4" />
            {t('aiChat:call.endCall')}
          </Button>
        </div>
      )}
    </div>
  );
}
